import { ExTooltip, TooltipPosition } from '@boomi/exosphere';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { useEffect, useMemo } from 'react';
import translations from '../../../../../../translations';
import type { PageCondition, PageOperation, PageRule } from '../../../../../../types/page';
import { isNullOrEmpty } from '../../../../../../utils/guard';
import ButtonAction from '../../../../../buttons/ButtonAction';
import DraggableTable, { type TableColumnList } from '../../../../../generic/DraggableTable';
import Toggle from '../../../../../inputs/Toggle';
import { SUMMARY_CONTEXT } from '../../../../constants';
import { usePageEditor } from '../../../PageEditorProvider';
import { usePageCondition } from '../PageConditionsProvider';
import {
    getAllPageConditionValues,
    getFormattedSummary,
    getIDsFromPageComponents,
    getIDsFromPageConditions,
} from './pageConditionsUtils';
import { useNotificationDispatch } from '../../../../../notifications/NotificationProvider';

type PageConditionListItem = PageCondition | PageRule | PageOperation;

const PageConditionItemList = ({
    listContext,
    onBeforeEditItem,
}: {
    listContext: string;
    onBeforeEditItem?: () => void;
}) => {
    const { state: pageBuilderState } = usePageEditor();
    const { page } = pageBuilderState;
    const { pageConditions = [], pageComponents = [] } = page;
    const {
        state: pageConditionState,
        updateCondition,
        updateConditionIndex,
        updateRuleIndex,
        updateOperationIndex,
        reorderConditions,
        reorderRules,
        reorderOperations,
        deleteCondition,
        deleteRule,
        deleteOperation,
        toggleConditionIsActive,
        updateRefreshValues,
        updateValues,
    } = usePageCondition();
    const { condition, refreshValues, values } = pageConditionState;

    const dispatchNotification = useNotificationDispatch();

    // Fetching extra info for any values that are used in the page conditions/rule/operations.
    const fetchValues = async (valuesIDs: string[]) => {
        try {
            const allValues = await getAllPageConditionValues(valuesIDs);
            updateValues(allValues);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: `${translations.PAGE_BUILDER_summary_error}: ${(error as Error).message}`,
                },
            });

            updateValues(null);
        } finally {
            updateRefreshValues(false);
        }
    };

    const title =
        listContext === SUMMARY_CONTEXT['CONDITIONS']
            ? 'Conditions'
            : listContext === SUMMARY_CONTEXT['RULES']
              ? 'Rules'
              : listContext === SUMMARY_CONTEXT['OPERATIONS']
                ? 'Operations'
                : null;

    // Gather all the bits we need to generate the summaries.
    // When adding or editing a rule/operation we can create new values so these should also be included.
    const componentsIDs = useMemo(() => getIDsFromPageComponents(pageComponents), [pageComponents]);
    const conditionsIDs = useMemo(() => getIDsFromPageConditions(pageConditions), [pageConditions]);
    const brandNewIDs = useMemo(
        () => getIDsFromPageConditions(condition !== null ? [condition] : null),
        [condition],
    );
    const combinedIDs = [...conditionsIDs, ...componentsIDs, ...brandNewIDs];
    const uniqueIDs = [...new Set(combinedIDs)] as string[];
    const data =
        listContext === SUMMARY_CONTEXT['CONDITIONS']
            ? pageConditions
            : listContext === SUMMARY_CONTEXT['RULES']
              ? condition?.pageRules
              : listContext === SUMMARY_CONTEXT['OPERATIONS']
                ? condition?.pageOperations
                : null;

    const items = (data ?? []) as PageConditionListItem[];

    // Generating summaries is expensive so don't do it unless we have all the bits that we need.
    const basicDataIsAvailable = !!data?.length && !!pageComponents?.length && !!listContext;
    const valueDataIsAvailable = uniqueIDs?.length ? !refreshValues && !!values?.length : true;
    const summaries =
        basicDataIsAvailable && valueDataIsAvailable
            ? getFormattedSummary(data, values, pageComponents, listContext)
            : null;

    // We have items to show but their summaries are not ready yet.
    const isLoading = !!items.length && !summaries?.length;

    // biome-ignore lint/correctness/useExhaustiveDependencies: Treat warnings as errors, fix later
    useEffect(() => {
        if (uniqueIDs?.length && refreshValues) {
            fetchValues(uniqueIDs);
        }
    }, [condition]);

    const onEditItem = (index: number) => {
        switch (listContext) {
            case SUMMARY_CONTEXT['CONDITIONS']: {
                onBeforeEditItem?.();
                updateConditionIndex(index);

                if (pageConditions) {
                    updateCondition(pageConditions[index]);
                }
                break;
            }
            case SUMMARY_CONTEXT['RULES']: {
                updateRuleIndex(index);
                break;
            }
            case SUMMARY_CONTEXT['OPERATIONS']: {
                updateOperationIndex(index);
                break;
            }
        }
    };

    const onDeleteItem = (index: number) => {
        switch (listContext) {
            case SUMMARY_CONTEXT['CONDITIONS']: {
                deleteCondition(index);
                break;
            }
            case SUMMARY_CONTEXT['RULES']: {
                deleteRule(index);
                break;
            }
            case SUMMARY_CONTEXT['OPERATIONS']: {
                deleteOperation(index);
                break;
            }
        }
    };

    const onReorderItems = (reorderedItems: PageConditionListItem[]) => {
        switch (listContext) {
            case SUMMARY_CONTEXT['CONDITIONS']: {
                reorderConditions(reorderedItems as PageCondition[]);
                break;
            }
            case SUMMARY_CONTEXT['RULES']: {
                reorderRules(reorderedItems as PageRule[]);
                break;
            }
            case SUMMARY_CONTEXT['OPERATIONS']: {
                reorderOperations(reorderedItems as PageOperation[]);
                break;
            }
        }
    };

    // Conditions saved before this flag existed have no value, which is treated as active.
    const isConditionActive = (index: number) => pageConditions?.[index]?.isActive !== false;

    const columns: TableColumnList<PageConditionListItem> = [
        {
            renderHeader: () => title,
            renderCell: ({ rowIndex }) => summaries?.[rowIndex] ?? null,
            cellClassName: 'generic-cell-overflow',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ rowIndex }) => (
                <div className="item-actions">
                    {listContext === SUMMARY_CONTEXT['CONDITIONS'] && (
                        <ExTooltip position={TooltipPosition.TOP} wrap>
                            <span slot="anchor">
                                <Toggle
                                    id={`condition-is-active-${rowIndex}`}
                                    testId={`condition-is-active-${rowIndex}`}
                                    ariaLabel={
                                        translations.PAGE_BUILDER_page_condition_active_label
                                    }
                                    isOn={isConditionActive(rowIndex)}
                                    onChange={() => toggleConditionIsActive(rowIndex)}
                                />
                            </span>
                            <span slot="content">
                                {translations.PAGE_BUILDER_page_condition_active_tooltip}
                            </span>
                        </ExTooltip>
                    )}
                    <ButtonAction
                        className="btn-tertiary"
                        onClick={() => onEditItem(rowIndex)}
                        title="Edit"
                    >
                        <PencilSimple size={18} />
                    </ButtonAction>
                    <ButtonAction
                        className="btn-tertiary danger"
                        onClick={() => onDeleteItem(rowIndex)}
                        title="Delete"
                    >
                        <Trash size={18} />
                    </ButtonAction>
                </div>
            ),
            size: '11rem',
        },
    ];

    if (isNullOrEmpty(data)) {
        return (
            <div className="group">
                <h4 className="group-heading">{title}</h4>
                <p className="group-message">No data</p>
            </div>
        );
    }

    return (
        <DraggableTable
            columns={columns}
            items={items}
            isLoading={isLoading}
            testId={`page-condition-${listContext.toLowerCase()}`}
            // Scoped by list context to prevent drag drop interactions between the
            // conditions, rules and operations lists.
            tableId={`PAGE_CONDITION_${listContext}`}
            onReorder={onReorderItems}
        />
    );
};

export default PageConditionItemList;
